import { useMemo } from 'react';

// Same store links rushmore-server hands the app (mobile.config.*-store-url).
export const APP_STORE_URL = 'https://apps.apple.com/app/id6744982141';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.ptoa.rushmore';
export const ANDROID_PACKAGE = 'com.ptoa.rushmore';

// The app's registered custom scheme (app.json "scheme"). Only good for
// opening the app to its home: the app routes paths from https links, and a
// custom-scheme path would parse as a hostname instead.
export const APP_SCHEME_URL = 'rushmore-social://';

const APEX_HOST = 'ptoarushmore.com';
const WWW_HOST = 'www.ptoarushmore.com';

/** Share paths the app handles: /ur/:id, /u/:id, /r/:id. */
export type ShareKind = 'ur' | 'u' | 'r';

export const useDevice = () => {
  const ua = useMemo(
    () => (typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : ''),
    []
  );

  const isAndroid = /android/.test(ua);
  // iPadOS reports itself as a Mac; the touch check tells it apart.
  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (/macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);
  const isMobile = isAndroid || isIOS;

  return { isAndroid, isIOS, isMobile };
};

/**
 * Link that opens this share in the app.
 *
 * iOS: Safari keeps a tap on a same-domain link in the browser, so the link
 * points at the OTHER associated domain (apex <-> www). Both are listed in
 * the app's associated domains and the AASA file, so iOS hands it to the app;
 * without the app installed it simply loads this same page there.
 *
 * Android: a Chrome intent pinned to the app's package, falling back to the
 * Play Store when it is not installed.
 */
export const buildOpenInAppUrl = (
  kind: ShareKind,
  id: string,
  platform: { isIOS: boolean; isAndroid: boolean }
): string => {
  const path = `/${kind}/${encodeURIComponent(id)}`;

  if (platform.isAndroid) {
    return (
      `intent://${APEX_HOST}${path}` +
      `#Intent;scheme=https;package=${ANDROID_PACKAGE};` +
      `S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`
    );
  }

  const currentHost = typeof window !== 'undefined' ? window.location.hostname : APEX_HOST;
  const otherHost = currentHost === WWW_HOST ? APEX_HOST : WWW_HOST;
  return `https://${otherHost}${path}`;
};
